import logger from '../utils/logger';
import { cachedRequest } from '../utils/apiCache';

const ALADHAN_API_BASE = 'https://api.aladhan.com/v1';

export const fetchPrayerTimes = async (settings, targetDate = null) => {
    try {
        if (!settings?.coordinates?.lat || !settings?.coordinates?.lon) {
            logger.warn('Missing coordinates for prayer times');
            return { times: {}, isAdjusted: false };
        }

        // Use provided date or default to today
        const date = targetDate || new Date();
        
        // Format date in YYYY-MM-DD format for the cache key
        const dateStr = date.toISOString().split('T')[0];

        // Create a cache key based on the settings and date
        const cacheKey = `prayer_times_${settings.coordinates.lat}_${settings.coordinates.lon}_${settings.calculationMethod}_${dateStr}`;

        // Extract forceFresh from settings if provided
        const forceFresh = settings.forceFresh || false;

        return await cachedRequest(cacheKey, async () => {
            const params = new URLSearchParams({
                latitude: settings.coordinates.lat,
                longitude: settings.coordinates.lon,
                method: settings.calculationMethod || 2,
                // Format date in DD-MM-YYYY format for the API
                date: date.toLocaleDateString('en-GB').split('/').join('-')
            });

            // No longer sending tunedTimes to the API - we'll handle adjustments locally
            let isAdjusted = false;

            if (settings.timezone) {
                params.append('timezone', settings.timezone);
            }

            logger.debug('Fetching prayer times with params:', params.toString(), 'for date:', dateStr);

            const response = await fetch(`${ALADHAN_API_BASE}/timings?${params}`);
            
            if (!response.ok) {
                throw new Error(`Prayer times API error: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.code === 200 && data.data && data.data.timings) {
                const timings = data.data.timings;
                return {
                    times: {
                        Fajr: timings.Fajr,
                        Dhuhr: timings.Dhuhr,
                        Asr: timings.Asr,
                        Maghrib: timings.Maghrib,
                        Isha: timings.Isha
                    },
                    isAdjusted
                };
            }

            throw new Error('Invalid response format from prayer times API');
        }, {
            // Cache prayer times for 12 hours
            cacheDuration: 12 * 60 * 60 * 1000,
            // Use forceFresh parameter if provided
            forceFresh
        });

    } catch (error) {
        logger.error('Error fetching prayer times:', error, 'for date:', targetDate);
        throw error;
    }
};

// Add calculation method constants
export const CALCULATION_METHODS = {
    MUSLIM_WORLD_LEAGUE: 3, // Muslim World League
    ISLAMIC_SOCIETY_NA: 2, // Islamic Society of North America
    EGYPTIAN: 5, // Egyptian General Authority of Survey
    UMM_AL_QURA: 4, // Umm Al-Qura University, Makkah
    KARACHI: 1, // University of Islamic Sciences, Karachi
    TEHRAN: 7, // Institute of Geophysics, University of Tehran
    GULF: 8, // Gulf Region
    KUWAIT: 9, // Kuwait
    QATAR: 10, // Qatar
    SINGAPORE: 11, // Majlis Ugama Islam Singapura, Singapore
    FRANCE: 12, // Union Organization islamic de France
    TURKEY: 13, // Diyanet İşleri Başkanlığı, Turkey
    RUSSIA: 14, // Spiritual Administration of Muslims of Russia
    MOONSIGHTING_COMMITTEE: 15, // Moonsighting Committee
    DUBAI: 16, // Dubai, UAE (Batoul Apps custom)
    MALAYSIA: 17, // Jabatan Kemajuan Islam Malaysia (JAKIM)
    TUNISIA: 18, // Tunisia
    ALGERIA: 19, // Algeria
    INDONESIA: 20, // Kementerian Agama Republik Indonesia
    MOROCCO: 21, // Morocco
    PORTUGAL: 22 // Comunidate Islamica de Lisboa (Portugal)
};
